/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { ActionType, deprecated } from "typesafe-actions";
const { createStandardAction } = deprecated;
import {
  ProcessAction,
  ProcessUpdatesTableType,
  SortDirection,
} from "@centralwebteam/narwhal";
import {
  ProcessUpdatesPerfSortColumn,
  ProcessUpdatePerf,
  ProcessUpdatesDetailsInnerTableSortColumn,
  ProcessUpdatesDetailsSortColumn,
} from "@/store/state";
import { ProcessUpdatePresentation } from "@/presentation/processUpdates";
import { JobPresentation } from "@/presentation/Job";
export const processUpdatesActions = {
  fetchingStarted: createStandardAction("[process updates] fetching data")(),
  fetchingFinished: createStandardAction(
    "[process updates] finished fetching data"
  )<{
    to: string;
  }>(),
  processActionsFetched: createStandardAction(
    "[process updates][saga] process actions fetched"
  )<{
    events: ProcessAction[];
    from: string;
    to: string;
  }>(),
  processActionsDeltaFetched: createStandardAction(
    "[process updates][saga] process actions delta fetched"
  )<{
    events: ProcessAction[];
    from: string;
    to: string;
  }>(),
  setCalculatedProcessUpdates: createStandardAction(
    "[process updates][saga] set calculated process updates"
  )<{
    processUpdatesPerf: MappyAbsolute<ProcessUpdatePerf>;
    processUpdateDetails: MappyAbsolute<ProcessUpdatePresentation[]>;
    processUpdateDetailsMore: MappyAbsolute<
      MappyAbsolute<ProcessUpdatePresentation[]>
    >;
  }>(),
  // characteristic table
  characteristicPerfHeaderCellClicked: createStandardAction(
    "[process updates] characteristic perf header cell clicked"
  )<{
    sortColumn: ProcessUpdatesPerfSortColumn;
    sortDirection: SortDirection;
  }>(),
  characteristicDetailsTableHeaderCellClicked: createStandardAction(
    "[process updates] characteristic details table header cell clicked"
  )<[ProcessUpdatesDetailsSortColumn, SortDirection]>(),
  characteristicInnerTableHeaderCellClicked: createStandardAction(
    "[process updates] characteristic inner table header cell clicked"
  )<[ProcessUpdatesDetailsInnerTableSortColumn, SortDirection]>(),
  characteristicRowClicked: createStandardAction(
    "[process updates] characteristic row clicked"
  )<{ name: string }>(),
  characteristicExpandAllClicked: createStandardAction(
    "[process updates] characteristic expand all clicked"
  )<string[]>(),
  characteristicCollapseAllClicked: createStandardAction(
    "[process updates] characteristic collapse all clicked"
  )<void>(),
  characteristicDetailsTableRowClicked: createStandardAction(
    "[process updates] characteristic details table row clicked"
  )<{ name: string }>(),
  characteristicDetailsTableExpandAllClicked: createStandardAction(
    "[process updates] characteristic details table expand all clicked"
  )<string[]>(),
  characteristicDetailsTableCollapseAllClicked: createStandardAction(
    "[process updates] characteristic details table collapse all clicked"
  )<string[]>(),
  // job table
  jobPerfHeaderCellClicked: createStandardAction(
    "[process updates] job perf header cell clicked"
  )<{
    sortColumn: ProcessUpdatesPerfSortColumn;
    sortDirection: SortDirection;
  }>(),
  jobDetailsTableHeaderCellClicked: createStandardAction(
    "[process updates] job details table header cell clicked"
  )<[ProcessUpdatesDetailsSortColumn, SortDirection]>(),
  jobInnerTableHeaderCellClicked: createStandardAction(
    "[process updates] job inner table header cell clicked"
  )<[ProcessUpdatesDetailsInnerTableSortColumn, SortDirection]>(),
  jobRowClicked: createStandardAction("[process updates] job row clicked")<{
    name: string;
  }>(),
  jobExpandAllClicked: createStandardAction(
    "[process updates] job expand all clicked"
  )<string[]>(),
  jobCollapseAllClicked: createStandardAction(
    "[process updates] job collapse all clicked"
  )<void>(),
  jobDetailsTableRowClicked: createStandardAction(
    "[process updates] job details table row clicked"
  )<{ name: string }>(),
  jobDetailsTableExpandAllClicked: createStandardAction(
    "[process updates] job details table expand all clicked"
  )<string[]>(),
  jobDetailsTableCollapseAllClicked: createStandardAction(
    "[process updates] job details table collapse all clicked"
  )<string[]>(),
  // machine table
  machineRowClicked: createStandardAction(
    "[process updates] machine row clicked"
  )<{ name: string }>(),
  machineExpandAllClicked: createStandardAction(
    "[process updates] machine expand all clicked"
  )<string[]>(),
  machineCollapseAllClicked: createStandardAction(
    "[process updates] machine collapse all clicked"
  )<void>(),
  machineDetailsTableRowClicked: createStandardAction(
    "[process updates] machine details table row clicked"
  )<{ name: string }>(),
  machineDetailsTableExpandAllClicked: createStandardAction(
    "[process updates] machine details table expand all clicked"
  )<string[]>(),
  machineDetailsTableCollapseAllClicked: createStandardAction(
    "[process updates] machine details table collapse all clicked"
  )<string[]>(),
  machineDetailsTableHeaderCellClicked: createStandardAction(
    "[process updates] machine details table header cell clicked"
  )<[ProcessUpdatesDetailsSortColumn, SortDirection]>(),
  machineInnerTableHeaderCellClicked: createStandardAction(
    "[process updates] machine inner table header cell clicked"
  )<[ProcessUpdatesDetailsInnerTableSortColumn, SortDirection]>(),
  machinePerfHeaderCellClicked: createStandardAction(
    "[process updates] machine perf header cell clicked"
  )<{
    sortColumn: ProcessUpdatesPerfSortColumn;
    sortDirection: SortDirection;
  }>(),
  // generic all tables
  detailsTableInnerTableRowClicked: createStandardAction(
    "[process updates] details table inner table row clicked"
  )<{ processUpdate: ProcessUpdatePresentation }>(),
  detailsTableInnerTableRowClickedRedirect: createStandardAction(
    "[process updates] details table inner table row clicked redirect"
  )<{ job: JobPresentation; processUpdate: ProcessUpdatePresentation }>(),
  detailsTableInnerTableRowClickedSetJobDetails: createStandardAction(
    "[process updates] details table inner table row clicked set job details"
  )<{ job: JobPresentation }>(),
  processUpdatesTableTypeChanged: createStandardAction(
    "[process updates] process updates table type changed"
  )<{ value: ProcessUpdatesTableType }>(),
};

export type ProcessUpdatesActions = ActionType<typeof processUpdatesActions>;
